import React, { useCallback, useMemo } from 'react'

import {
  StaffAIViewMode,
  useSessionPreviewTitle,
  useStaffAIAssistant,
} from 'src/components/StaffAIAssistant/StaffAIAssistantContext'
import { useGetStaffAIChatSessionsQuery } from 'src/fetch/staffAIChat'
import { Button, HStack, Icon, T, Tooltip, VStack } from 'src/govwell-ui'

type Props = {
  onClose: () => void
  viewMode: StaffAIViewMode
  onMaximize?: () => void
  onMinimize?: () => void
  showSidebar?: boolean
}

const StaffAIChatHeader = ({
  onClose,
  viewMode,
  onMaximize,
  onMinimize,
  showSidebar = true,
}: Props) => {
  const context = useStaffAIAssistant()
  const { sessions } = useGetStaffAIChatSessionsQuery()

  const activeSession = useMemo(() => {
    if (!context?.activeSessionId) {
      return null
    }
    return sessions.find((s) => s.id === context.activeSessionId) ?? null
  }, [context?.activeSessionId, sessions])

  const title = useSessionPreviewTitle(activeSession)

  const handleToggleSize = useCallback(() => {
    if (viewMode === 'side') {
      onMaximize?.()
    } else {
      onMinimize?.()
    }
  }, [viewMode, onMaximize, onMinimize])

  const toggleIcon =
    viewMode === 'side' ? 'FaUpRightAndDownLeftFromCenter' : 'FaDownLeftAndUpRightToCenter'
  const toggleTooltip = viewMode === 'side' ? 'View as full page' : 'Minimize to side view'

  return (
    <div className="shrink-0 border-b border-neutral-200 bg-white px-4 py-3">
      <HStack gap={3}>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-900">
          <Icon.FaSparkles className="h-4 w-4 text-white" />
        </div>
        <VStack gap={0} className="min-w-0 flex-1">
          <HStack gap={2}>
            <T.Body
              className={`truncate font-semibold text-neutral-900 ${showSidebar ? 'max-w-md' : 'max-w-[180px]'}`}
            >
              {title}
            </T.Body>
            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              Beta
            </span>
          </HStack>
          <T.BodySmall className="text-neutral-500">GovWell AI Assistant</T.BodySmall>
        </VStack>
        <Tooltip content={toggleTooltip}>
          <span className="shrink-0">
            <Button
              variant="default"
              size="sm"
              icon={toggleIcon}
              onClick={handleToggleSize}
              ariaLabel={toggleTooltip}
            />
          </span>
        </Tooltip>
        <Button
          variant="default"
          size="sm"
          icon="FaXmark"
          onClick={onClose}
          ariaLabel="Close"
          className="shrink-0"
        />
      </HStack>
    </div>
  )
}

export default React.memo(StaffAIChatHeader)
